/**
 * Categories — manage page.
 *
 * DataTable + Sheet CRUD over persistent mock state for product categories.
 * Supports parent category assignment for subcategory trees. Soft-delete
 * sets isActive to false so historical references keep working.
 */

import { useMemo, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { FolderTree, Pencil, Plus, Trash2, Undo2 } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockCategories,
  type MockCategory,
} from "../../../lib/catalogMock";

export const Route = createFileRoute("/_app/settings/categories")({
  component: CategoriesPage,
});

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function CategoriesPage() {
  const [categories, setCategories] = usePersistentMockState<MockCategory[]>(
    "categories",
    mockCategories,
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<MockCategory | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<MockCategory | null>(null);

  const activeCategories = useMemo(
    () => categories.filter((c) => c.isActive),
    [categories],
  );

  const parentById = useMemo(
    () => new Map(categories.map((c) => [c._id, c.displayName])),
    [categories],
  );

  const childCountByParent = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of categories) {
      if (c.parentCategoryId) {
        map.set(c.parentCategoryId, (map.get(c.parentCategoryId) ?? 0) + 1);
      }
    }
    return map;
  }, [categories]);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: MockCategory) {
    setEditing(row);
    setSheetOpen(true);
  }

  function handleSubmit(values: CategoryFormValues) {
    if (editing) {
      setCategories((prev) =>
        prev.map((c) =>
          c._id === editing._id
            ? {
                ...c,
                displayName: values.displayName,
                parentCategoryId: values.parentCategoryId,
                sortOrder: values.sortOrder,
                isActive: values.isActive,
              }
            : c,
        ),
      );
      toast.success("Category updated. Audit trail recorded.");
    } else {
      const newId = `cat_${Date.now()}`;
      setCategories((prev) => [
        ...prev,
        {
          _id: newId,
          code: values.code,
          displayName: values.displayName,
          parentCategoryId: values.parentCategoryId,
          sortOrder: values.sortOrder,
          isActive: values.isActive,
        },
      ]);
      toast.success("Category created (mock)");
    }
    setSheetOpen(false);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    const target = archiveTarget;
    setCategories((prev) =>
      prev.map((c) =>
        c._id === target._id ? { ...c, isActive: false } : c,
      ),
    );
    setArchiveTarget(null);
    toast.success("Category archived");
  }

  function handleRestore(row: MockCategory) {
    setCategories((prev) =>
      prev.map((c) =>
        c._id === row._id ? { ...c, isActive: true } : c,
      ),
    );
    toast.success("Category restored");
  }

  const columns = useMemo<DataTableColumn<MockCategory>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Name",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "parentCategory",
        header: "Parent",
        cell: (info) => {
          const pid = info.row.original.parentCategoryId;
          return (
            <span className="text-(--text-secondary)">
              {pid ? parentById.get(pid) ?? "—" : "—"}
            </span>
          );
        },
      },
      {
        id: "subcategories",
        header: "Subcategories",
        cell: (info) => (
          <Badge variant="neutral" size="sm">
            {childCountByParent.get(info.row.original._id) ?? 0}
          </Badge>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => (
          <Badge
            variant={info.row.original.isActive ? "success" : "neutral"}
            size="sm"
          >
            {info.row.original.isActive ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1 justify-end">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(row);
                }}
                aria-label="Edit"
              >
                <Pencil className="size-3.5" />
              </Button>
              {row.isActive ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArchiveTarget(row);
                  }}
                  aria-label="Archive"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(row);
                  }}
                  aria-label="Restore"
                >
                  <Undo2 className="size-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [parentById, childCountByParent],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Categories"
        description="Product categorisation tree. Used for reporting, pricing, and filtering across the catalog."
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add category
          </Button>
        }
      />

      <DataTable
        data={categories}
        columns={columns}
        emptyTitle="No categories yet"
        emptyDescription="Add your first category to organise the product catalog."
        emptyIcon={<FolderTree className="size-5 text-(--text-tertiary)" />}
        emptyAction={
          <Button onClick={openCreate}>
            <Plus className="size-4" /> Add category
          </Button>
        }
      />

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>
              {editing ? "Edit category" : "New category"}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the category details. Code is locked once created."
                : "Codes are used for reporting — keep them short and stable."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CategoryForm
              key={editing?._id ?? "new"}
              isEdit={Boolean(editing)}
              categories={activeCategories}
              excludeId={editing?._id ?? null}
              initialValues={
                editing
                  ? {
                      code: editing.code,
                      displayName: editing.displayName,
                      parentCategoryId: editing.parentCategoryId,
                      sortOrder: editing.sortOrder,
                      isActive: editing.isActive,
                    }
                  : undefined
              }
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Archive confirmation dialog */}
      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              Archive {archiveTarget?.displayName}?
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const childCount = archiveTarget
                  ? (childCountByParent.get(archiveTarget._id) ?? 0)
                  : 0;
                return (
                  <>
                    {childCount > 0 ? (
                      <span className="block mb-2 text-(--warning)">
                        {childCount} subcategory(ies) reference this as
                        parent. They will keep their reference but this
                        category will be hidden from new assignments.
                      </span>
                    ) : null}
                    This category will be marked inactive and hidden from
                    new product assignments.
                  </>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmArchive}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Inline form
 * ───────────────────────────────────────────────────────────── */

type CategoryFormValues = {
  code: string;
  displayName: string;
  parentCategoryId: string | null;
  sortOrder: number;
  isActive: boolean;
};

function CategoryForm({
  initialValues,
  isEdit = false,
  categories,
  excludeId,
  onSubmit,
  onCancel,
}: {
  initialValues?: Partial<CategoryFormValues>;
  isEdit?: boolean;
  categories: MockCategory[];
  excludeId: string | null;
  onSubmit: (values: CategoryFormValues) => void;
  onCancel?: () => void;
}) {
  const [code, setCode] = useState(initialValues?.code ?? "");
  const [displayName, setDisplayName] = useState(
    initialValues?.displayName ?? "",
  );
  const [parentCategoryId, setParentCategoryId] = useState<string | null>(
    initialValues?.parentCategoryId ?? null,
  );
  const [sortOrder, setSortOrder] = useState<string>(
    initialValues?.sortOrder?.toString() ?? "0",
  );
  const [isActive, setIsActive] = useState(initialValues?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Filter out the current category (and its descendants) from parent options
  const parentOptions = useMemo(() => {
    return categories.filter((c) => c._id !== excludeId);
  }, [categories, excludeId]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!isEdit && !code.trim()) errs.code = "Code is required.";
    if (!displayName.trim()) errs.displayName = "Name is required.";
    const so = Number.parseInt(sortOrder, 10);
    if (Number.isNaN(so) || so < 0) errs.sortOrder = "Must be 0 or higher.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({
      code: code.trim().toUpperCase(),
      displayName: displayName.trim(),
      parentCategoryId: parentCategoryId || null,
      sortOrder: so,
      isActive,
    });
  }

  return (
    <Form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormItem>
        <FormLabel>Code</FormLabel>
        <FormControl>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. ELEC"
            disabled={isEdit}
          />
        </FormControl>
        {errors.code ? <FormMessage>{errors.code}</FormMessage> : null}
      </FormItem>

      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Electronics"
          />
        </FormControl>
        {errors.displayName ? (
          <FormMessage>{errors.displayName}</FormMessage>
        ) : null}
      </FormItem>

      <FormItem>
        <FormLabel>Parent category</FormLabel>
        <Select
          value={parentCategoryId ?? "__none__"}
          onValueChange={(v) =>
            setParentCategoryId(v === "__none__" ? null : v)
          }
        >
          <FormControl>
            <SelectTrigger>
              <SelectValue placeholder="None (top-level)" />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            <SelectItem value="__none__">None (top-level)</SelectItem>
            {parentOptions.map((c) => (
              <SelectItem key={c._id} value={c._id}>
                {c.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormItem>

      <FormItem>
        <FormLabel>Sort order</FormLabel>
        <FormControl>
          <Input
            type="number"
            min={0}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </FormControl>
        {errors.sortOrder ? (
          <FormMessage>{errors.sortOrder}</FormMessage>
        ) : null}
      </FormItem>

      <FormItem className="flex items-center justify-between">
        <FormLabel>Active</FormLabel>
        <Switch checked={isActive} onCheckedChange={setIsActive} />
      </FormItem>

      <div className="flex justify-end gap-2 pt-4 border-t border-(--border-subtle)">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit">
          {isEdit ? "Save changes" : "Create category"}
        </Button>
      </div>
    </Form>
  );
}
